// Sons do Estandarte, seguindo a página de conceito "Uma mesa de guerra": madeira,
// papel, dados num copo de couro, aço, trompas e tambores. Música, dados e choque de
// espadas usam gravações CC0 de public/audio/ quando existem; o resto (e o plano B
// quando falta arquivo) é sintetizado no navegador (Web Audio).

import { readJson, writeJson } from '../lib/storage';
import { FADE, MusicDirector, type MusicPlayer, type Scene } from './music';
import { SampleBank, type MusicTrack, type SampleGroup } from './samples';
import type { GameEvent } from '@estandarte/engine';

export type SoundName =
  | 'click'
  | 'carta'
  | 'troca'
  | 'turno'
  | 'selecionar'
  | 'peca'
  | 'marcha'
  | 'barco'
  | 'dados'
  | 'aco'
  | 'impacto'
  | 'conquista'
  | 'eliminacao'
  | 'boss'
  | 'bossDano'
  | 'bossMorte'
  | 'raid'
  | 'runa'
  | 'fogo'
  | 'gelo'
  | 'pocao'
  | 'moedas'
  | 'amuleto'
  | 'serverSave'
  | 'vitoria'
  | 'derrota';

export type Bus = 'sfx' | 'ui';

export interface SoundSettings {
  master: number;
  music: number;
  sfx: number;
  ui: number;
  muted: boolean;
  /** Trilha de fundo ligada. */
  musicOn: boolean;
}

const SETTINGS_KEY = 'estandarte.sound.v1';
const DEFAULTS: SoundSettings = { master: 0.7, music: 0.35, sfx: 0.9, ui: 0.6, muted: false, musicOn: true };

let settings: SoundSettings = { ...DEFAULTS, ...(readJson<Partial<SoundSettings>>(SETTINGS_KEY) ?? {}) };
const listeners = new Set<(s: SoundSettings) => void>();

export function getSoundSettings(): SoundSettings {
  return settings;
}

export function setSoundSettings(patch: Partial<SoundSettings>): void {
  settings = { ...settings, ...patch };
  writeJson(SETTINGS_KEY, settings);
  applyLevels();
  if (patch.musicOn !== undefined || patch.muted !== undefined) syncMusicOn();
  for (const l of listeners) l(settings);
}

export function onSoundSettings(fn: (s: SoundSettings) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ------------------------------------------------------------------ grafo de áudio

let AC: AudioContext | null = null;
let master: GainNode;
let musicBus: GainNode;
let musicDuck: GainNode;
let buses: Record<Bus, GainNode>;
let rev: ConvolverNode;
let NB: AudioBuffer;
/** Destino da chamada atual (permite tocar mais baixo sem mexer nas funções). */
let target: AudioNode;
/** Variação de tom da chamada atual (±4% nos sons frequentes). */
let pitch = 1;

function impulse(ac: AudioContext, sec: number, decay: number): AudioBuffer {
  const len = Math.floor(ac.sampleRate * sec);
  const b = ac.createBuffer(2, len, ac.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = b.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return b;
}

/** Cria o contexto na primeira interação (política de autoplay dos navegadores). */
function ac(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!AC) {
    const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return null;
    try {
      AC = new C();
    } catch {
      return null;
    }
    master = AC.createGain();
    const comp = AC.createDynamicsCompressor();
    master.connect(comp);
    comp.connect(AC.destination);
    rev = AC.createConvolver();
    rev.buffer = impulse(AC, 1.8, 3);
    const wet = AC.createGain();
    wet.gain.value = 0.25;
    rev.connect(wet);
    wet.connect(master);
    musicDuck = AC.createGain();
    musicBus = AC.createGain();
    musicBus.connect(musicDuck);
    musicDuck.connect(master);
    buses = { sfx: AC.createGain(), ui: AC.createGain() };
    buses.sfx.connect(master);
    buses.ui.connect(master);
    NB = AC.createBuffer(1, AC.sampleRate * 2, AC.sampleRate);
    const d = NB.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    applyLevels();
    void bank.decode(AC);
  }
  if (AC.state === 'suspended') void AC.resume();
  return AC;
}

function applyLevels() {
  if (!AC) return;
  const t = AC.currentTime;
  master.gain.setTargetAtTime(settings.muted ? 0 : settings.master, t, 0.05);
  musicBus.gain.setTargetAtTime(settings.music, t, 0.05);
  buses.sfx.gain.setTargetAtTime(settings.sfx, t, 0.05);
  buses.ui.gain.setTargetAtTime(settings.ui, t, 0.05);
}

function out(n: AudioNode, wet?: boolean) {
  n.connect(target);
  if (wet !== false) n.connect(rev);
}

interface ToneOpts {
  t?: number;
  f: number;
  f2?: number;
  dur?: number;
  g?: number;
  a?: number;
  type?: OscillatorType;
  detune?: number;
  filt?: { type?: BiquadFilterType; f: number; q?: number };
  wet?: boolean;
}

function tone(o: ToneOpts) {
  const A = AC!;
  const now = A.currentTime + (o.t ?? 0);
  const dur = o.dur ?? 0.3;
  const g = o.g ?? 0.2;
  const a = o.a ?? 0.005;
  const osc = A.createOscillator();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(o.f * pitch, now);
  if (o.f2) osc.frequency.exponentialRampToValueAtTime(o.f2 * pitch, now + dur);
  if (o.detune) osc.detune.value = o.detune;
  const gn = A.createGain();
  gn.gain.setValueAtTime(0.0001, now);
  gn.gain.exponentialRampToValueAtTime(g, now + a);
  gn.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  let n: AudioNode = osc;
  if (o.filt) {
    const fl = A.createBiquadFilter();
    fl.type = o.filt.type ?? 'lowpass';
    fl.frequency.value = o.filt.f;
    fl.Q.value = o.filt.q ?? 1;
    osc.connect(fl);
    n = fl;
  }
  n.connect(gn);
  out(gn, o.wet);
  osc.start(now);
  osc.stop(now + dur + 0.05);
}

interface NoiseOpts {
  t?: number;
  dur?: number;
  g?: number;
  a?: number;
  f?: number;
  f2?: number;
  q?: number;
  type?: BiquadFilterType;
  wet?: boolean;
}

function noise(o: NoiseOpts) {
  const A = AC!;
  const now = A.currentTime + (o.t ?? 0);
  const dur = o.dur ?? 0.2;
  const g = o.g ?? 0.2;
  const a = o.a ?? 0.003;
  const s = A.createBufferSource();
  s.buffer = NB;
  const fl = A.createBiquadFilter();
  fl.type = o.type ?? 'bandpass';
  fl.frequency.setValueAtTime((o.f ?? 1000) * pitch, now);
  if (o.f2) fl.frequency.exponentialRampToValueAtTime(o.f2 * pitch, now + dur);
  fl.Q.value = o.q ?? 1;
  const gn = A.createGain();
  gn.gain.setValueAtTime(0.0001, now);
  gn.gain.exponentialRampToValueAtTime(g, now + a);
  gn.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  s.connect(fl);
  fl.connect(gn);
  out(gn, o.wet);
  s.start(now, Math.random());
  s.stop(now + dur + 0.05);
}

const bell = (t: number, f: number, dur: number, g: number) =>
  [1, 2.76, 5.4, 8.93].forEach((m, i) => tone({ t, f: f * m, dur: dur / (i * 0.6 + 1), g: g / (i + 1), a: 0.002 }));
const knock = (t: number, f: number, g = 0.35) => {
  tone({ t, f, f2: f * 0.6, dur: 0.09, g, a: 0.002, wet: false });
  noise({ t, dur: 0.03, g: g * 0.5, f: Math.min(f * 6, 9000), q: 2, wet: false });
};
const brass = (t: number, f: number, dur: number, g = 0.06) =>
  [-7, 0, 7].forEach((d) => tone({ t, type: 'sawtooth', f, dur, a: 0.045, g, filt: { f: f * 4, q: 1 }, detune: d }));
const drum = (t: number, g = 0.55) => {
  tone({ t, f: 85, f2: 48, dur: 0.4, g, a: 0.003 });
  noise({ t, dur: 0.12, g: 0.2, type: 'lowpass', f: 500 });
};
const coins = (t: number, n = 7) => {
  for (let i = 0; i < n; i++) {
    const f = 3000 + Math.random() * 2600;
    const tt = t + i * 0.055 + Math.random() * 0.03;
    tone({ t: tt, f, dur: 0.14, g: 0.05, a: 0.001 });
    tone({ t: tt, f: f * 1.47, dur: 0.09, g: 0.025, a: 0.001 });
  }
};
const horn = (t: number, f: number, dur: number, g = 0.12) => {
  tone({ t, type: 'sawtooth', f, f2: f * 1.04, dur, a: 0.35, g, filt: { f: 650, q: 1.2 } });
  tone({ t: t + 0.05, type: 'sawtooth', f: f * 1.5, f2: f * 1.53, dur: dur * 0.9, a: 0.4, g: g * 0.5, filt: { f: 800, q: 1 } });
};

/** `short`: versão curta, para turnos de outros jogadores. */
const S: Record<SoundName, (short: boolean) => void> = {
  click: () => {
    noise({ dur: 0.025, g: 0.18, f: 2600, q: 3, wet: false });
    tone({ f: 900, f2: 600, dur: 0.04, g: 0.05, wet: false });
  },
  carta: () => noise({ dur: 0.24, a: 0.03, g: 0.25, f: 3200, f2: 900, q: 0.8 }),
  troca: () => {
    coins(0);
    knock(0.42, 140, 0.5);
    noise({ t: 0.42, dur: 0.06, g: 0.2, type: 'lowpass', f: 900, wet: false });
  },
  turno: () => {
    bell(0, 659.3, 1.5, 0.12);
    bell(0.24, 880, 1.7, 0.1);
  },
  selecionar: () => noise({ dur: 0.14, a: 0.02, g: 0.12, f: 5200, f2: 2600, q: 2 }),
  peca: () => {
    knock(0, 190, 0.45);
    knock(0.045, 430, 0.12);
  },
  marcha: (short) => {
    for (let i = 0; i < (short ? 3 : 6); i++) {
      knock(i * 0.17, 85 + Math.random() * 25, 0.3);
      noise({ t: i * 0.17, dur: 0.06, g: 0.08, type: 'lowpass', f: 700, wet: false });
    }
  },
  barco: (short) => {
    noise({ dur: short ? 1.2 : 2.4, a: 0.6, g: 0.28, type: 'lowpass', f: 380, f2: 900 });
    if (!short) bell(0.5, 880, 1.8, 0.07);
  },
  dados: (short) => {
    let t = 0;
    const n = short ? 6 : 10;
    for (let i = 0; i < n; i++) {
      t += 0.035 + Math.random() * 0.05 + i * 0.009;
      knock(t, 650 + Math.random() * 1100, 0.28 * (1 - i / (n + 3)));
    }
  },
  aco: () => {
    noise({ dur: 0.05, g: 0.28, f: 4200, q: 1 });
    [1180, 1730, 2620, 3390, 4410].forEach((f, i) => tone({ f, dur: 0.65 - i * 0.08, g: 0.06, a: 0.001 }));
  },
  impacto: () => {
    tone({ f: 115, f2: 45, dur: 0.3, g: 0.6, a: 0.003, wet: false });
    noise({ dur: 0.16, g: 0.3, type: 'lowpass', f: 650, wet: false });
  },
  conquista: (short) => {
    drum(0);
    drum(0.17, 0.45);
    if (short) return;
    noise({ t: 0.28, dur: 0.55, a: 0.05, g: 0.07, f: 1400, q: 5 });
    brass(0.36, 392, 0.24, 0.06);
    brass(0.6, 523.25, 0.9, 0.07);
  },
  eliminacao: () => {
    drum(0, 0.6);
    horn(0.1, 98, 2.4, 0.13);
  },
  boss: () => {
    horn(0, 73.4, 2.8, 0.14);
    noise({ t: 0.15, dur: 3, a: 0.08, g: 0.5, type: 'lowpass', f: 220, f2: 70 });
    tone({ t: 0.15, f: 41, dur: 2.6, g: 0.35, a: 0.2 });
  },
  bossDano: () => {
    S.impacto(false);
    [320, 507, 690].forEach((f, i) => tone({ t: 0.01, f, dur: 0.5, g: 0.05 / (i + 1), a: 0.002 }));
  },
  bossMorte: () => {
    [1, 1.48, 2.03, 2.71, 3.6].forEach((m, i) => tone({ f: 110 * m, dur: 3.6 - i * 0.4, g: 0.14 / (i + 1), a: 0.003 }));
    noise({ dur: 0.08, g: 0.3, f: 900, q: 1 });
    coins(1.1);
  },
  raid: () => {
    noise({ dur: 2.6, a: 0.3, g: 0.6, type: 'lowpass', f: 150 });
    tone({ f: 38, dur: 2.6, g: 0.4, a: 0.3 });
    for (let i = 0; i < 7; i++) knock(0.3 + Math.random() * 1.8, 60 + Math.random() * 80, 0.3);
  },
  runa: () => {
    [880, 1108.7, 1318.5, 1760, 2217].forEach((f, i) => tone({ t: i * 0.055, f, dur: 1.3, g: 0.05, a: 0.004 }));
    noise({ dur: 0.9, a: 0.1, g: 0.04, type: 'highpass', f: 7000 });
  },
  fogo: () => {
    noise({ dur: 0.9, a: 0.12, g: 0.4, f: 400, f2: 2400, q: 0.7 });
    noise({ t: 0.15, dur: 0.8, a: 0.05, g: 0.25, type: 'lowpass', f: 900 });
  },
  gelo: () => {
    [2093, 2637, 3136, 4186].forEach((f, i) => {
      tone({ t: i * 0.07, f, dur: 0.9, g: 0.045, a: 0.001 });
      tone({ t: i * 0.07, f: f * 1.01, dur: 0.9, g: 0.03, a: 0.001 });
    });
    noise({ dur: 0.4, g: 0.08, type: 'highpass', f: 6000 });
  },
  pocao: () => {
    tone({ f: 1300, f2: 420, dur: 0.05, g: 0.15, wet: false });
    for (let i = 0; i < 3; i++) tone({ t: 0.12 + i * 0.13, f: 240 + i * 40, f2: 520 + i * 50, dur: 0.1, g: 0.22, wet: false });
  },
  moedas: () => coins(0),
  amuleto: (short) => {
    for (let i = 0; i < (short ? 2 : 6); i++) knock(i * 0.5, i % 2 ? 1900 : 1450, 0.15);
  },
  serverSave: () => {
    for (let i = 0; i < 3; i++) bell(i * 1.15, 196, 3.2, 0.17);
  },
  vitoria: () => {
    brass(0, 261.6, 0.2);
    brass(0.2, 329.6, 0.2);
    brass(0.4, 392, 0.2);
    brass(0.62, 523.25, 1.6, 0.07);
    brass(0.62, 659.3, 1.6, 0.05);
    drum(0.62, 0.5);
  },
  derrota: () => {
    [440, 392, 329.6].forEach((f, i) => tone({ t: i * 0.42, type: 'triangle', f, dur: 1.1, g: 0.14, a: 0.01 }));
    tone({ t: 1.26, type: 'triangle', f: 220, dur: 2, g: 0.1, a: 0.05 });
  },
};

/** Sons frequentes ganham variação de tom para não soarem iguais. */
const FREQUENT = new Set<SoundName>(['click', 'carta', 'selecionar', 'peca', 'dados', 'aco', 'impacto', 'marcha']);
/** Sons importantes abaixam a trilha por um instante. */
const IMPORTANT = new Set<SoundName>(['conquista', 'eliminacao', 'boss', 'bossMorte', 'raid', 'serverSave', 'vitoria', 'derrota']);

export interface PlayOpts {
  /** Turno de bot ou de outro jogador: mais baixo (−6 dB) e versão curta. */
  quiet?: boolean;
  /** Atraso em segundos. */
  delay?: number;
  bus?: Bus;
}

export function play(name: SoundName, opts: PlayOpts = {}): void {
  if (settings.muted) return;
  const A = ac();
  if (!A) return;
  const run = () => {
    try {
      const g = A.createGain();
      g.gain.value = opts.quiet ? 0.5 : 1;
      g.connect(buses[opts.bus ?? (name === 'click' || name === 'selecionar' ? 'ui' : 'sfx')]);
      target = g;
      pitch = FREQUENT.has(name) ? 0.96 + Math.random() * 0.08 : 1;
      if (!(name === 'aco' && sample('clash', g, 0))) S[name](!!opts.quiet);
      if (IMPORTANT.has(name)) duck();
    } catch {
      /* áudio indisponível: segue em silêncio */
    } finally {
      pitch = 1;
    }
  };
  if (opts.delay) setTimeout(run, opts.delay * 1000);
  else run();
}

function duck() {
  if (!AC) return;
  const t = AC.currentTime;
  musicDuck.gain.cancelScheduledValues(t);
  musicDuck.gain.setTargetAtTime(0.25, t, 0.05);
  musicDuck.gain.setTargetAtTime(1, t + 1.6, 0.6);
}

// ------------------------------------------------------------------ gravações

const bank = new SampleBank(
  typeof import.meta.env?.BASE_URL === 'string' ? import.meta.env.BASE_URL : '/',
  (url) => fetch(url),
  (mime) => typeof Audio !== 'undefined' && new Audio().canPlayType(mime) !== '',
);

/** Toca uma variação do grupo com ±4% de tom; false se não há gravação (use o sintetizado). */
function sample(group: SampleGroup, dest: AudioNode, at: number, maxDur?: number): boolean {
  const buf = AC && bank.pick(group);
  if (!AC || !buf) return false;
  const src = AC.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = 0.96 + Math.random() * 0.08;
  const t = AC.currentTime + at;
  const g = AC.createGain();
  src.connect(g);
  g.connect(dest);
  if (maxDur) {
    g.gain.setValueAtTime(1, t + maxDur - 0.05);
    g.gain.linearRampToValueAtTime(0.0001, t + maxDur);
    src.start(t);
    src.stop(t + maxDur + 0.02);
  } else src.start(t);
  return true;
}

/** Atraso entre o lançamento do atacante e o do defensor. */
export const DEFENDER_THROW_DELAY = 0.08;
/** Duração do chacoalhar antes do lançamento. */
export const SHAKE = 0.3;

/**
 * Dados: chacoalhar (~0,3 s), lançamento do atacante e, ~80 ms depois, o do defensor.
 * `quiet` (turno dos outros): só os lançamentos, mais baixos. Devolve quando os dados
 * param de rolar (para encaixar aço/golpe) e cai no sintetizado sem gravação.
 */
export function playDice(opts: { quiet?: boolean; delay?: number; defender?: boolean } = {}): number {
  if (settings.muted) return 0.85;
  const A = ac();
  if (!A) return 0.85;
  const run = () => {
    if (!bank.has('diceThrow')) return play('dados', { quiet: opts.quiet });
    const g = A.createGain();
    g.gain.value = opts.quiet ? 0.5 : 1;
    g.connect(buses.sfx);
    const shake = !opts.quiet && bank.has('diceShake') && sample('diceShake', g, 0, SHAKE);
    const t0 = shake ? SHAKE : 0;
    sample('diceThrow', g, t0);
    if (opts.defender !== false) sample('diceThrow', g, t0 + DEFENDER_THROW_DELAY);
  };
  if (opts.delay) setTimeout(run, opts.delay * 1000);
  else run();
  return bank.has('diceThrow') ? (opts.quiet ? 0.5 : 0.8) : 0.85;
}

// ------------------------------------------------------------------ trilha

interface Voice {
  el: HTMLAudioElement;
  gain: GainNode;
  track: MusicTrack;
  /** Já começou o crossfade do loop. */
  looped: boolean;
}

let voices: Voice[] = [];
let wanted: MusicTrack | null = null;

function fadeOutVoice(v: Voice, fade: number) {
  const A = AC!;
  const t = A.currentTime;
  v.gain.gain.cancelScheduledValues(t);
  v.gain.gain.setValueAtTime(v.gain.gain.value, t);
  v.gain.gain.linearRampToValueAtTime(0.0001, t + fade);
  voices = voices.filter((x) => x !== v);
  setTimeout(() => {
    v.el.pause();
    v.el.removeAttribute('src');
  }, fade * 1000 + 100);
}

function startVoice(track: MusicTrack, fade: number) {
  const A = ac();
  const url = bank.musicUrl(track);
  if (!A || !url) return;
  const el = new Audio(url);
  el.preload = 'auto';
  const gain = A.createGain();
  gain.gain.value = 0.0001;
  try {
    A.createMediaElementSource(el).connect(gain);
  } catch {
    return;
  }
  gain.connect(musicBus);
  const v: Voice = { el, gain, track, looped: false };
  // Loop com crossfade: perto do fim entra outra cópia da faixa desde o começo.
  el.addEventListener('timeupdate', () => {
    if (v.looped || !el.duration || !isFinite(el.duration)) return;
    if (el.duration - el.currentTime <= FADE.loop && voices.includes(v)) {
      v.looped = true;
      startVoice(track, FADE.loop);
      fadeOutVoice(v, FADE.loop);
    }
  });
  el.addEventListener('ended', () => voices.includes(v) && fadeOutVoice(v, 0.05));
  voices.push(v);
  const t = A.currentTime;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.linearRampToValueAtTime(1, t + Math.max(fade, 0.05));
  el.play().catch(() => {
    /* autoplay bloqueado: tenta de novo no próximo clique */
    voices = voices.filter((x) => x !== v);
  });
}

/** Faixa que de fato toca: sem o arquivo do boss, a batalha continua. */
function resolveTrack(to: MusicTrack | null): MusicTrack | null {
  if (to && !bank.musicUrl(to) && to === 'boss') return 'battle';
  return to;
}

const webPlayer: MusicPlayer = {
  crossfade(to, fade) {
    wanted = to;
    if (!AC) return;
    void bank.load().then(() => {
      if (wanted !== to) return;
      const real = settings.musicOn && !settings.muted ? resolveTrack(to) : null;
      for (const v of [...voices]) if (v.track !== real) fadeOutVoice(v, fade);
      if (real && !voices.some((v) => v.track === real)) startVoice(real, fade);
    });
  },
};

const director = new MusicDirector(webPlayer);

/** Menu/sala, partida ou fim de jogo. */
export function setMusicScene(scene: Scene, hasBoss = false): void {
  director.setScene(scene, hasBoss);
}

/** Crossfades da partida: boss surgiu, morreu, fez raid ou a partida acabou. */
export function musicEvents(events: GameEvent[]): void {
  director.onEvents(events);
}

/** Primeira interação: cria o contexto e começa a faixa pedida (autoplay). */
export function unlockMusic(): void {
  if (!ac()) return;
  if (wanted && !voices.length) webPlayer.crossfade(wanted, FADE.scene);
}

function syncMusicOn() {
  if (!AC) return;
  if (!settings.musicOn || settings.muted) for (const v of [...voices]) fadeOutVoice(v, 0.4);
  else if (wanted && !voices.length) webPlayer.crossfade(wanted, 1);
}
